/**
 * WindMapPage Component
 *
 * This is the wind map page that displays the WindBarbMap component
 * showing wind speed and direction for all weather stations.
 *
 * Key Features:
 * - Responsive layout that adapts to different screen sizes
 * - Interactive wind barb map display
 * - Collapsible side drawer for station list
 * - Pin functionality to keep drawer open
 * - Backdrop overlay for better UX
 */

import React, { useState } from "react";
import {
  Box,
  IconButton,
  Drawer,
  Backdrop,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PushPinIcon from "@mui/icons-material/PushPin";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import WindBarbMap from "../components/WindBarbMap/WindBarbMap";
import AllStationsList from "../components/AllStations/AllStationsList";

function WindMapPage() {
  // State management for drawer visibility and pin status
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pinned, setPinned] = useState(false);

  // Responsive design: Adjust drawer width based on screen size
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const drawerWidth = isSmallScreen ? "70%" : "350px";

  // Toggle drawer visibility
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Toggle pin status to keep drawer open
  const togglePin = () => {
    setPinned(!pinned);
  };

  return (
    <>
      {/* Main container with wind barb map */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          position: "relative",
        }}
      >
        {/* Wind barb map container */}
        <Box sx={{ p: 2, width: "100%", height: "100%" }}>
          <WindBarbMap />
        </Box>

        {/* Expand menu button with tooltip */}
        <Tooltip title="Expand Menu" placement="left">
          <IconButton
            onClick={toggleDrawer}
            sx={{
              position: "fixed",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: "30px",
              height: "80px",
              backgroundColor: "black",
              color: "white",
              borderRadius: "5px 0 0 5px",
              "&:hover": {
                backgroundColor: "black",
              },
              zIndex: 1200,
            }}
          >
            <ArrowLeftIcon />
          </IconButton>
        </Tooltip>

        {/* Backdrop overlay when drawer is open */}
        <Backdrop
          open={drawerOpen && !pinned}
          onClick={toggleDrawer}
          sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }}
        />

        {/* Side drawer for station list */}
        <Drawer
          anchor="right"
          open={drawerOpen || pinned}
          onClose={toggleDrawer}
          variant="persistent"
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Drawer controls */}
            <IconButton onClick={toggleDrawer}>
              <ChevronRightIcon />
            </IconButton>
            <IconButton onClick={togglePin}>
              <PushPinIcon color={pinned ? "primary" : "inherit"} />
            </IconButton>
            {/* Station list component */}
            <AllStationsList />
          </Box>
        </Drawer>
      </Box>
    </>
  );
}

export default WindMapPage;
